import { CondensedRallyTimeline_ContextOnly } from "./CondensedRallyTimeline_ContextOnly";

import * as React from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import type { KitchenArrivalRow } from "./RallyBoardPopup";
import type {
  PlayerIndexMap,
  RenderPlayerIdentity,
} from "./GameStoryDialog_UPDATED";
import type { FirefightClip } from "./GameStory_firefights";

import { ActiveRallyVideo } from "./ActiveRallyVideo";
import { RallyShotQualityStrip } from "./GameStory_RallyQualityStrip";
import { RallyShotQualityMiniChart } from "./GameStory_RallyShotQualityMiniChart";
import { formatOverlayShotQuality } from "./GameStory_shotQuality";
import { InsightsJson } from "../../shared/types/insightsEtcTypes";
// import { CvJson } from "../../server/routes/cvBallMap";
import { CvJson } from "../../shared/types/cvJson";

import {
  buildSingleTopFocusCard,
  SingleTopFocusCard,
} from "./buildSingleTopFocus";
import { TITLE_NEXT_TIME_FOCUS } from "./App";

export function SingleTopFocusPopup(props: {
  open: boolean;
  onClose: () => void;
  packageId: string;
  rows: KitchenArrivalRow[];
  insightsJson: InsightsJson | null;
  cvJson: CvJson | null | undefined;
  renderPlayerIdentity: RenderPlayerIdentity;
  insightsToCvPlayerIndex: PlayerIndexMap;
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
}) {
  const {
    open,
    onClose,
    packageId,
    rows,
    insightsJson,
    cvJson,
    renderPlayerIdentity,
    insightsToCvPlayerIndex,
    firefightsClips,
    firefightRallySet,
  } = props;

  const [selectedPlayerId, setSelectedPlayerId] = React.useState<0 | 1 | 2 | 3>(
    0,
  );
  const [selectedEvidenceIndex, setSelectedEvidenceIndex] = React.useState(0);
  const [currentShotNumber, setCurrentShotNumber] = React.useState<
    number | null
  >(null);

  const focusCard: SingleTopFocusCard | null = React.useMemo(() => {
    if (!insightsJson) return null;
    return buildSingleTopFocusCard({
      insightsJson,
      cvJson,
      selectedPlayerId,
    });
  }, [insightsJson, cvJson, selectedPlayerId]);

  React.useEffect(() => {
    setSelectedEvidenceIndex(0);
  }, [selectedPlayerId, focusCard?.label]);

  const selectedEvidence = focusCard?.evidence?.[selectedEvidenceIndex] ?? null;
  const selectedRow = selectedEvidence
    ? (rows.find((r) => r.rallyIndex === selectedEvidence.rallyIndex) ?? null)
    : null;

  const seedLoopRegion = React.useMemo(() => {
    if (!selectedRow || !selectedEvidence) return undefined;

    const segStartMs = selectedRow.segmentStartMs ?? 0;
    const segEndMs = selectedRow.segmentEndMs ?? segStartMs;

    const prePadMs = 500;
    const postPadMs = 900;

    const rawStartMs = selectedEvidence.startMs ?? segStartMs;
    const rawEndMs = selectedEvidence.endMs ?? rawStartMs;

    const absStartMs = Math.max(segStartMs, rawStartMs - prePadMs);
    const absEndMs = Math.min(segEndMs, rawEndMs + postPadMs);

    const aSec = Math.max(0, (absStartMs - segStartMs) / 1000);
    const bSec = Math.max(aSec + 0.25, (absEndMs - segStartMs) / 1000);

    return {
      enabled: true,
      aSec,
      bSec,
      nonce: selectedEvidence.rallyIndex * 1000 + selectedEvidence.shotIndex,
    };
  }, [selectedRow, selectedEvidence]);

  const selectedEvidenceWhyLines = React.useMemo(() => {
    if (!selectedEvidence?.whyItCounts) return [];
    return selectedEvidence.whyItCounts
      .split("•")
      .map((s) => s.trim())
      .filter(Boolean);
  }, [selectedEvidence]);

  const activeShotOverlayText = React.useMemo(() => {
    if (!selectedEvidence) return undefined;

    const parts: string[] = [];

    if (selectedEvidence.ruleLabel?.trim()) {
      parts.push(selectedEvidence.ruleLabel.trim());
    } else if (selectedEvidence.whyItCounts?.trim()) {
      parts.push(selectedEvidence.whyItCounts.trim());
    }

    return parts[0] ?? undefined;
  }, [selectedEvidence]);

  const shotOverlayPersistentExtraText = React.useCallback(
    (overlay: any) => {
      if (!selectedEvidence) return undefined;
      if (overlay?.shotNumber !== selectedEvidence.shotNumber) return undefined;

      return selectedEvidence.ruleLabel?.trim() || undefined;
    },
    [selectedEvidence],
  );

  const TEXT_PRIMARY = "rgba(0,0,0,0.9)";
  const TEXT_SECONDARY = "rgba(0,0,0,0.72)";
  const TEXT_TERTIARY = "rgba(0,0,0,0.58)";
  const BORDER = "1px solid rgba(0,0,0,0.5)";
  const BORDER_SOFT = "1px solid rgba(0,0,0,0.08)";
  const SURFACE_SUBTLE = "rgba(0,0,0,0.018)";
  const SURFACE_SELECTED = "rgba(0,0,0,0.035)";

  if (!open) return null;

  return (
    <Paper
      elevation={10}
      sx={{
        position: "fixed",
        left: 18,
        top: 50,
        width: 1380,
        maxWidth: "calc(100vw - 36px)",
        height: "95vh",
        maxHeight: "calc(100vh - 120px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        zIndex: 2000,
        borderRadius: 3,
        border: BORDER,
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: BORDER, // "1px solid rgba(0,0,0,0.12)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25 }}>
          <Box sx={{ fontSize: 18, fontWeight: 900, color: TEXT_PRIMARY }}>
            {/* One Thing Before Next Play */}
            {TITLE_NEXT_TIME_FOCUS}
          </Box>
          <Box sx={{ fontSize: 12, color: TEXT_TERTIARY }}>
            {focusCard?.evidence?.length ?? 0} evidence rows
          </Box>
        </Box>
        <Button
          size="small"
          variant="text"
          onClick={onClose}
          startIcon={<CloseIcon />}
          sx={{ textTransform: "none", fontWeight: 900 }}
        >
          Close
        </Button>
      </Box>

      <Box
        sx={{
          px: 1.2,
          py: 0.75,
          borderBottom: BORDER_SOFT,
          bgcolor: SURFACE_SUBTLE,
          fontSize: 12.5,
          lineHeight: 1.35,
          color: TEXT_SECONDARY,
        }}
      >
        <Box sx={{ fontWeight: 900, color: TEXT_PRIMARY, mb: 0.25 }}>
          Experiment note
        </Box>
        The purpose of this experiment was to pick one concrete thing a player should review before the next game, using deterministic evidence instead of a broad list of advice. The strongest idea is the constraint: one player, one priority, a few clips. Where it got stuck is confidence; the selected focus can feel arbitrary unless the evidence and wording make the choice obvious.
      </Box>

      <Box
        sx={{
          px: 1.2,
          py: 0.8,
          display: "flex",
          alignItems: "center",
          gap: 0.8,
          flexWrap: "wrap",
          borderBottom: "1px solid rgba(0,0,0,0.10)",
          bgcolor: SURFACE_SUBTLE,
        }}
      >
        <Box sx={{ fontSize: 14, fontWeight: 700, color: TEXT_TERTIARY }}>
          Player
        </Box>
        {[0, 1, 2, 3].map((pid) => {
          const selected = selectedPlayerId === pid;
          return (
            <Button
              key={pid}
              size="small"
              variant="text"
              onClick={() => setSelectedPlayerId(pid as 0 | 1 | 2 | 3)}
              sx={{
                textTransform: "none",
                minHeight: 28,
                py: 0.25,
                px: 0.55,
                minWidth: 0,
                borderRadius: "9px",
                border: selected ?  BORDER : "1px solid transparent",
                bgcolor: selected ? SURFACE_SELECTED : "transparent",
              }}
            >
              {renderPlayerIdentity({
                playerId: pid as 0 | 1 | 2 | 3,
                labelOnly: false,
                size: 34,
                borderRadius: 5,
                fontSize: 12,
              })}
            </Button>
          );
        })}
      </Box>

      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            width: 390,
            maxWidth: "38%",
            borderRight: "1px solid rgba(0,0,0,0.12)",
            overflow: "auto",
            p: 1,
          }}
        >
          <Box
            sx={{
              fontSize: 22,
              lineHeight: 1.05,
              fontWeight: 700,
              mb: 0.75,
              color: TEXT_PRIMARY,
            }}
          >
            {focusCard?.displayLabel ?? "No focus available"}
          </Box>

          {/* <Box
            sx={{
              fontSize: 18,
              lineHeight: 1.25,
              mb: 1.1,
              color: TEXT_SECONDARY,
            }}
          >
            {focusCard?.shortInstruction ?? ""}
          </Box> */}

          {focusCard?.whyThisWon?.map((line, idx) => (
            <Box
              key={idx}
              sx={{
                fontSize: 14,
                lineHeight: 1.32,
                mb: 0.55,
                color: TEXT_SECONDARY,
              }}
            >
              • {line}
            </Box>
          ))}
          <Box
            sx={{
              mt: 1.4,
              mb: 0.75,
              fontSize: 14,
              fontWeight: 700,
              color: TEXT_PRIMARY,
            }}
          >
            Supporting evidence
          </Box>

          {(focusCard?.evidence ?? []).map((ev, idx) => {
            const selected = idx === selectedEvidenceIndex;
            const whyLines = (ev.whyItCounts ?? "")
              .split("•")
              .map((s) => s.trim())
              .filter(Boolean);

            return (
              <Box
                key={`${ev.rallyIndex}-${ev.shotIndex}-${idx}`}
                onClick={() => setSelectedEvidenceIndex(idx)}
                sx={{
                  p: 1.1,
                  mb: 0.7,
                  borderRadius: "10px",
                  cursor: "pointer",
                  border: selected ? "1px solid rgba(0,0,0,0.18)" : BORDER_SOFT,
                  bgcolor: selected ? SURFACE_SELECTED : "white",
                  transition:
                    "background-color 120ms ease, border-color 120ms ease",
                }}
              >
                <Box
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: TEXT_PRIMARY,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    flexWrap: "wrap",
                    mb: 0.4,
                  }}
                >
                  <Box>Rally {ev.rallyIndex + 1}</Box>
                  <Box sx={{ color: TEXT_TERTIARY }}>•</Box>
                  <Box>Shot {ev.shotNumber}</Box>
                  <Box sx={{ color: TEXT_TERTIARY }}>•</Box>
                  {renderPlayerIdentity({
                    playerId: ev.playerId as 0 | 1 | 2 | 3,
                    labelOnly: false,
                    size: 22,
                    borderRadius: 4,
                    fontSize: 14,
                  })}
                  {ev.ruleLabel ? <Box>{ev.ruleLabel}</Box> : null}
                </Box>

                <Box
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.32,
                    color: TEXT_SECONDARY,
                    mb: 0.45,
                  }}
                >
                  {whyLines.slice(0, 2).map((line, i) => (
                    <Box key={i}>{line}</Box>
                  ))}
                </Box>
                {!!ev.scoreFactors?.length && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.35,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    {ev.scoreFactors.slice(0, 3).map((factor, factorIdx) => (
                      <Box
                        key={`${factor.label}-${factorIdx}`}
                        title={factor.detail}
                        sx={{
                          px: 0.55,
                          py: 0.12,
                          borderRadius: "999px",
                          border: "1px solid rgba(0,0,0,0.12)",
                          bgcolor: "rgba(0,0,0,0.035)",
                          color: TEXT_TERTIARY,
                          fontSize: 10,
                          fontWeight: 800,
                          lineHeight: 1.2,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {factor.label} {factor.value.toFixed(1)}
                      </Box>
                    ))}
                  </Box>
                )}
                {/* {ev.ruleLabel ? (
                  <Box
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: TEXT_PRIMARY,
                    }}
                  >
                   {ev.ruleLabel}
                  </Box>
                ) : null} */}
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              p: 1.25,
              borderBottom: BORDER,
              bgcolor: SURFACE_SUBTLE,
            }}
          >
            {selectedRow && selectedEvidence ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.6,
                    flexWrap: "wrap",
                    mb: 0.65,
                  }}
                >
                  <Box
                    sx={{ fontSize: 18, fontWeight: 700, color: TEXT_PRIMARY }}
                  >
                    Rally {selectedRow.rallyIndex + 1}
                  </Box>

                  <Box sx={{ fontSize: 18, fontWeight: 700,color: TEXT_PRIMARY }}>•</Box>

                  {renderPlayerIdentity({
                    playerId: selectedEvidence.playerId as 0 | 1 | 2 | 3,
                    labelOnly: false,
                    size: 28,
                    borderRadius: 6,
                    fontSize: 18,
                  })}

                  <Box sx={{ fontSize: 18,  fontWeight: 700,color: TEXT_PRIMARY }}>•</Box>

                  <Box
                    sx={{ fontSize: 18, fontWeight: 700, color: TEXT_PRIMARY }}
                  >
                    Shot {selectedEvidence.shotNumber}
                  </Box>
                  {selectedEvidence?.ruleLabel ? (
                    <Box
                      sx={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: TEXT_PRIMARY,
                        mb: 0.25,
                      }}
                    >
                      • {selectedEvidence.ruleLabel}
                    </Box>
                  ) : null}

                  {/* {selectedEvidence.shotType ? (
          <>
            <Box sx={{ fontSize: 14, color: TEXT_TERTIARY }}>•</Box>
            <Box sx={{ fontSize: 14, color: TEXT_SECONDARY }}>
              {selectedEvidence.shotType}
            </Box>
          </>
        ) : null} */}
                </Box>

                {/* <Box
                  sx={{
                    fontSize: 22,
                    lineHeight: 1.08,
                    fontWeight: 900,
                    color: TEXT_PRIMARY,
                    mb: 0.55,
                  }}
                >
                  {focusCard?.displayLabel}
                </Box> */}

                {!!selectedEvidenceWhyLines.length && (
                  <Box sx={{ mb: 0.6 }}>
                    {selectedEvidenceWhyLines.map((line, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          fontSize: 14,
                          lineHeight: 1.35,
                          color: TEXT_PRIMARY, //  TEXT_SECONDARY,
                          mb: 0.2,
                        }}
                      >
                        {line}
                      </Box>
                    ))}
                  </Box>
                )}

                {!!selectedEvidence.scoreFactors?.length && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.45,
                      flexWrap: "wrap",
                      alignItems: "center",
                      mb: 0.2,
                    }}
                  >
                    {selectedEvidence.scoreFactors.map((factor, idx) => (
                      <Box
                        key={`${factor.label}-${idx}`}
                        title={factor.detail}
                        sx={{
                          px: 0.7,
                          py: 0.18,
                          borderRadius: "999px",
                          border: BORDER_SOFT,
                          bgcolor: "rgba(0,0,0,0.035)",
                          color: TEXT_SECONDARY,
                          fontSize: 11,
                          fontWeight: 800,
                          lineHeight: 1.2,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {factor.label}: {factor.value.toFixed(2)}
                      </Box>
                    ))}
                  </Box>
                )}

              </>
            ) : (
              <Box sx={{ fontSize: 14, fontWeight: 700, color: TEXT_TERTIARY }}>
                No evidence selected
              </Box>
            )}
          </Box>



          <Box sx={{ flex: 1, minHeight: 0, bgcolor: "black" }}>
            {selectedRow ? (
              <ActiveRallyVideo
                aiContextLabel="Single Top Focus"
                focusedFilter={null}
                packageId={packageId}
                row={selectedRow}
                rows={rows}
                // activeShotNumber={null}
                activeShotNumber={selectedEvidence?.shotNumber ?? null}
                seekToActiveShotOnChange={true}
                activeShotExtraText={activeShotOverlayText}
                shotOverlayPersistentExtraText={shotOverlayPersistentExtraText}
                showCondensedTimeline={false}
                fontColor="white"
                controlsPlacement="left"
                firefightsClips={firefightsClips}
                firefightRallySet={firefightRallySet}
                showRateControls={true}
                showTitle={false}
                showScoreEtc={false}
                showAi={true}
                aiDrawerMode="split"
                showLoopScrubber={true}
                showPopoutButton={true}
                isInFullPopup={false}
                renderPlayerIdentity={renderPlayerIdentity}
                insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                onPlaybackShotNumberChange={(shotNumber: number | null) => {
                  setCurrentShotNumber(shotNumber);
                }}
                seedLoopRegion={seedLoopRegion}
                defaultLoopUiOpen={true}
                defaultPlaybackRate={0.25}
              />
            ) : (
              <Box sx={{ p: 2, color: "rgba(255,255,255,0.75)" }}>
                No rally selected.
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
